import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import s from '../AdminStats.module.css';

const API = 'https://api.rasulova-academy.uz/api';

export const AdminStats = () => {
  const { isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  const [answers, setAnswers] = useState([]);
  const [users, setUsers] = useState({});
  const [courses, setCourses] = useState({});
  const [questions, setQuestions] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterKurs, setFilterKurs] = useState('all');

  useEffect(() => {
    if (!isAdmin) { navigate('/'); return; }
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const answersData = await fetch(`${API}/yozma-javob/`).then(r => r.json());
      setAnswers(answersData);

      const userIds = [...new Set(answersData.map(a => a.user))];
      const userResults = await Promise.all(
        userIds.map(id =>
          fetch(`${API}/users/${id}/`)
            .then(r => r.json())
            .catch(() => ({ id, first_name: '?', last_name: '', username: '?' }))
        )
      );
      const usersMap = {};
      userResults.forEach(u => { usersMap[u.id] = u; });
      setUsers(usersMap);

      const coursesData = await fetch(`${API}/kurslar/`).then(r => r.json());
      const coursesMap = {};
      coursesData.forEach(c => { coursesMap[c.id] = c; });
      setCourses(coursesMap);

      const kursIds = [...new Set(answersData.map(a => a.kurs))];
      const questionResults = await Promise.all(
        kursIds.map(id =>
          fetch(`${API}/kurs-yozma-savol/?kurs=${id}`)
            .then(r => r.json())
            .catch(() => [])
        )
      );
      const questionsMap = {};
      questionResults.flat().forEach(q => { questionsMap[q.id] = q; });
      setQuestions(questionsMap);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const kursIds = [...new Set(answers.map(a => a.kurs))];
  const filtered = filterKurs === 'all'
    ? answers
    : answers.filter(a => a.kurs === Number(filterKurs));

  const graded = filtered.filter(a => a.ball !== null);
  const avgBall = graded.length > 0
    ? (graded.reduce((sum, a) => sum + a.ball, 0) / graded.length).toFixed(1)
    : '—';

  if (loading) {
    return (
      <div className={s.loadingPage}>
        <div className={s.spinner} />
        <p>Yuklanmoqda...</p>
      </div>
    );
  }

  return (
    <div className={s.page}>
      <div className={s.header}>
        <div>
          <h1 className={s.title}>Admin — Yakuniy javoblar</h1>
          <p className={s.subtitle}>Talabalar yakuniy nazorat javoblari</p>
        </div>
        <button className={s.logoutBtn} onClick={() => { logout(); navigate('/login'); }}>
          Chiqish
        </button>
      </div>

      <div className={s.tabs}>
        <button
          className={`${s.tab} ${filterKurs === 'all' ? s.tabActive : ''}`}
          onClick={() => setFilterKurs('all')}
        >
          Barchasi ({answers.length})
        </button>
        {kursIds.map(id => (
          <button
            key={id}
            className={`${s.tab} ${filterKurs === String(id) ? s.tabActive : ''}`}
            onClick={() => setFilterKurs(String(id))}
          >
            {courses[id]?.nomi_uz || `Kurs ${id}`} ({answers.filter(a => a.kurs === id).length})
          </button>
        ))}
      </div>

      <div className={s.statsRow}>
        <div className={s.statCard}>
          <span className={s.statNum}>{filtered.length}</span>
          <span className={s.statLabel}>Jami javoblar</span>
        </div>
        <div className={s.statCard}>
          <span className={`${s.statNum} ${s.statGreen}`}>{graded.length}</span>
          <span className={s.statLabel}>Baholangan</span>
        </div>
        {/* <div className={s.statCard}>
          <span className={`${s.statNum} ${s.statOrange}`}>
            {filtered.filter(a => a.ball === null).length}
          </span>
          <span className={s.statLabel}>Kutilmoqda</span>
        </div> */}
        <div className={s.statCard}>
          <span className={`${s.statNum} ${s.statBlue}`}>{avgBall}</span>
          <span className={s.statLabel}>O'rtacha ball</span>
        </div>
      </div>

      <div className={s.list}>
        {filtered.length === 0 && <div className={s.empty}>Javoblar topilmadi</div>}
        {filtered.map(answer => {
          const user = users[answer.user];
          const question = questions[answer.savol];
          const isGraded = answer.ball !== null;

          return (
            <div key={answer.id} className={`${s.card} ${isGraded ? s.cardGraded : s.cardPending}`}>
              <div className={s.cardHeader}>
                <div className={s.studentInfo}>
                  <div className={s.avatar}>
                    {(user?.first_name?.[0] || '?').toUpperCase()}
                    {(user?.last_name?.[0] || '').toUpperCase()}
                  </div>
                  <div>
                    <div className={s.studentName}>
                      {user ? `${user.first_name} ${user.last_name}` : `Foydalanuvchi #${answer.user}`}
                    </div>
                    <div className={s.studentUsername}>@{user?.username || '—'}</div>
                  </div>
                </div>
                <div className={s.cardMeta}>
                  <span className={s.courseTag}>
                    {courses[answer.kurs]?.nomi_uz || `Kurs ${answer.kurs}`}
                  </span>
                </div>
              </div>

              {question && (
                <div className={s.questionBox}>
                  <span className={s.boxLabel}>Savol</span>
                  <div dangerouslySetInnerHTML={{ __html: question.savol_uz }} />
                </div>
              )}

              <div className={s.answerBox}>
                <span className={s.boxLabel}>Talaba javobi</span>
                <div className={s.answerContent} dangerouslySetInnerHTML={{ __html: answer.javob }} />
              </div>

              <div className={s.aiResult}>
                <div className={s.aiBall}>
                  <span className={s.boxLabel}>Ball</span>
                  <span className={isGraded ? s.ballValue : s.ballEmpty}>
                    {isGraded ? `${answer.ball} / 100` : '—'}
                  </span>
                </div>
                <div className={s.aiComment}>
                  <span className={s.boxLabel}>AI izohi</span>
                  <p className={s.commentText}>
                    {answer.komment || <em>Izoh yo'q</em>}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
